import React, {
  useState,
  useEffect,
  useReducer,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Box,
  Card,
  IconButton,
  Input,
  InputAdornment,
  Stack,
  Tooltip,
  Typography,
  Button,
  Switch,
  Divider,
  Chip,
  CircularProgress,
  Backdrop,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  Checkbox,
  TextField,
} from "@mui/material";
import {
  DarkMode,
  EmailOutlined,
  EmailRounded,
  EmailSharp,
  EmailTwoTone,
  RefreshOutlined,
  MarkEmailRead,
  AssignmentInd,
  LeakAdd,
} from "@mui/icons-material";
import RuleIcon from "@mui/icons-material/Rule";
import RuleFolderIcon from "@mui/icons-material/RuleFolder";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import RefreshIcon from "@mui/icons-material/Refresh";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AlarmOffIcon from "@mui/icons-material/AlarmOff";
import AddAlarmIcon from "@mui/icons-material/AddAlarm";
import LockIcon from "@mui/icons-material/Lock";
import currentUserToken from "CurrentUserToken";
import { get, post } from "aws-amplify/api";
import PostAddIcon from "@mui/icons-material/PostAdd";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import FilterListIcon from "@mui/icons-material/FilterList";
import { I18n } from "@aws-amplify/core";
import { APIStatusDialog } from "components/APIStatusDialog";
import ErrorDialog from "popup/ErrorDialog";
import dayjs from "dayjs";
import { darkenColor } from "assets/util-functions";
import LaunchIcon from "@mui/icons-material/Launch";
import InfoIcon from "@mui/icons-material/Info";
import { fetchAlarms, getCustomerForm } from "./../../apiService";
import { datagridColumnsWithGrouping } from "./columns";
import { datagridColumnsWithNoGrouping } from "./columns";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import {
  DataGridPro,
  GRID_ROOT_GROUP_ID,
  GridExpandMoreIcon,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_TREE_DATA_GROUPING_FIELD,
} from "@mui/x-data-grid-pro";
import ConfirmationReassign from "popup/ConfirmationReassign";
import { itIT } from "@mui/x-data-grid/locales";
import HistoryIcon from "@mui/icons-material/History";
import { deDE } from "@mui/x-data-grid/locales";
import DoDisturbOnIcon from "@mui/icons-material/DoDisturbOn";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import PushPinIcon from "@mui/icons-material/PushPin";
import AlarmsFilter from "../AlarmsFilter";
import ServerFilesMenu from "../ServerFilesMenu";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { format, max, set } from "date-fns";
import { useTemplateController } from "context";
import { Await } from "react-router-dom";
import ShowMailPopup from "popup/MailPopup";

const COLUMN_VISIBILITY_KEY = "columnVisibility_alarms";
const GROUPING_STATE_KEY = "groupingState";
const LANGUAGE_SWITCH_KEY = "languageSwitch";
const SEVERITY_SWITCH_KEY = "severitySwitch";
const COLUMN_WIDTHS_KEY = "columnWidths_alarms";

const handleColumnResize = (params) => {
  const { colDef, width } = params;
  const savedWidths = JSON.parse(localStorage.getItem(COLUMN_WIDTHS_KEY)) || {};
  savedWidths[colDef.field] = width;
  localStorage.setItem(COLUMN_WIDTHS_KEY, JSON.stringify(savedWidths));
};

// Function to get the column order key based on grouping
const getColumnsOrderKey = (grouping) =>
  `columnsOrder_alarms_${grouping ? "grouping" : "no_grouping"}`;

const loadFromLocalStorage = (key, defaultValue) => {
  const saved = localStorage.getItem(key);
  return saved ? JSON.parse(saved) : defaultValue;
};

const AlarmDataGrid = ({
  tableHeight,
  selectedRowDataPopup,
  profile,
  handlePopUpButtonClick,
  handleChargeConfirmationSave,
  handleReactiveAlarmSave,
  handlePopUpButtonClick2,
  handleRemoteConnection,
  resizeThrottle,
  language,
  onRowSelect,
  state,
  dispatch,
  alarmFilterSwitches,
  setAlarmFilterSwitches,
  alarmFilterDateRange,
  setAlarmFilterDateRange,
  setIsStepperAlarmDialogOpen,
  setIsLockDialogOpen,
  setIsClosureRulesDialogOpen,
  handleReassignAlarm,
  handleSuspendAlarm,
  handleQuickSuspendAlarm,
  handleTakeInCharge,
  handleCloseAlarm,
  handleAddRemark,
  handleOpenNewSalesforceCase,
  handleOpenSameStoreAlarm,
  handleOpenMailAlarm,
  selectedIcon,
  showPopup,
  showPopup2,
  deselect,
  setDeselect,
  fetchLists,
  isManualRefresh,
  setIsManualRefresh,
  handleRefresh,
  setIsEffectRunning,
  isAutoRefresh,
  isEffectRunning,
  searchText,
  setSearchText,
  filterDuration,
  setFilterDuration,
  quickSuspendDuration,
  setQuickSuspendDuration,
  accountSelected,
  setAccountSelected,
}) => {
  //const [searchText, setSearchText] = useState("");
  const apiRef = useGridApiRef();
  const styleAppliedRef = useRef(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [tableParentIsMD, setTableParentIsMD] = useState(false);
  const toolbarRef = useRef(null);
  const isExpandedAllOnce = useRef(false);
  const expansionState = useRef({});
  // Get initial grouping value
  const initialGrouping = (() => {
    const savedGrouping = localStorage.getItem(GROUPING_STATE_KEY);
    return savedGrouping !== null ? JSON.parse(savedGrouping) : true;
  })();

  const [controller] = useTemplateController();
  const { darkMode } = controller;
  const [isOpen, setIsOpen] = useState(false); //popup email
  const [mailContent, setMailContent] = useState("");
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);
  const [showSubmenu, setShowSubmenu] = useState(false);
  const closeTimeoutRef = useRef(null);

  const shiftItem = [
    { key: "", label: "All" },
    { key: "A", label: "A" },
    { key: "B", label: "B" },
    { key: "C", label: "C" },
    { key: "D", label: "D" },
    { key: "E", label: "E" },
    { key: "F", label: "F" },
    { key: "G", label: "G" },
  ];

  const controlerItem = [
    { key: "", label: I18n.get("All control and subsystems") },
    { key: "100", label: I18n.get("Refrigeration (Wurm systems only)") },
    { key: "010", label: I18n.get("Building management (Wurm systems only)") },
    { key: "001", label: I18n.get("Plug-ins (Wurm systems only)") },
    { key: "222", label: I18n.get("Others") }, //222 is a dummy value for the Others filter
  ];

  // const [selectedValue, setSelectedValue] = useState("");
  const [selectedShifts, setSelectedShifts] = useState(() => {
    const saved = JSON.parse(localStorage.getItem("selectedShifts")) || [];
    return saved.map((key) => {
      const found = shiftItem.find((item) => item.key === key);
      return found || { key, label: key };
    });
  });

  const [selectedControler, setSelectedControler] = useState(() => {
    const saved = JSON.parse(localStorage.getItem("selectedControler")) || [];
    return saved.map((key) => {
      const found = controlerItem.find((item) => item.key === key);
      return found || { key, label: key };
    });
  });

  const handleChangeShift = (event, newValue) => {
    const isAllNowSelected = newValue.some((item) => item.key === "");
    const hasOtherValues = newValue.some((item) => item.key !== "");
    console.log("newValue: ", newValue);

    if (isAllNowSelected && hasOtherValues) {
      const lastSelected = newValue[newValue.length - 1];
      if (lastSelected.key === "") {
        setSelectedShifts([{ key: "", label: "All" }]);
        localStorage.setItem("selectedShifts", JSON.stringify([""]));
      } else {
        const filtered = newValue.filter((item) => item.key !== "");
        setSelectedShifts(filtered);
        localStorage.setItem(
          "selectedShifts",
          JSON.stringify(filtered.map((item) => item.key)),
        );
      }
    } else if (isAllNowSelected) {
      setSelectedShifts([{ key: "", label: "All" }]);
      localStorage.setItem("selectedShifts", JSON.stringify([""]));
    } else {
      setSelectedShifts(newValue);
      localStorage.setItem(
        "selectedShifts",
        JSON.stringify(newValue.map((item) => item.key)),
      );
    }
  };

  const handleChangeControler = (event, newValue) => {
    const isAllNowSelected = newValue.some((item) => item.key === "");
    const isOthersSelected = newValue.some((item) => item.key === "222");
    const hasOtherValues = newValue.some((item) => item.key !== "");

    if (isAllNowSelected && hasOtherValues) {
      const lastSelected = newValue[newValue.length - 1];
      if (lastSelected.key === "") {
        setSelectedControler([{ key: "", label: "All" }]);
        localStorage.setItem("selectedControler", JSON.stringify([""]));
      } else {
        const filtered = newValue.filter((item) => item.key !== "");
        setSelectedControler(filtered);
        localStorage.setItem(
          "selectedControler",
          JSON.stringify(filtered.map((item) => item.key)),
        );
      }
    } else if (isAllNowSelected) {
      setSelectedControler([{ key: "", label: "All" }]);
      localStorage.setItem("selectedControler", JSON.stringify([""]));
    } else {
      setSelectedControler(newValue);
      localStorage.setItem(
        "selectedControler",
        JSON.stringify(newValue.map((item) => item.key)),
      );
    }
  };

  const handleOpenMailPopup = (value) => {
    setMailContent(value);
    setIsOpen(true);
  };
  const handleCloseMailPopup = () => {
    setIsOpen(false);
  };
  const stateOptions = [
    { id: 0, label: I18n.get("Inactive") },
    { id: 1, label: I18n.get("Active") },
    { id: 2, label: I18n.get("Closed") },
    { id: 3, label: I18n.get("Suspended") },
    { id: 4, label: I18n.get("Suspended With Lock") },
  ];

  // const [cInactive, setCInactive] = useState("#f2f2f2");
  // const [cActive, setCActive] = useState("#ffd6d6");
  // const [cClosed, setCClosed] = useState("#d6f5d6");
  // const [cSuspended, setCSuspended] = useState("#d6eaff");
  // const [cSuspendedLock, setCSuspendedLock] = useState("#d6eaff");
  // useEffect(() => {
  //   if (darkMode) {
  //     //darkMode=true
  //     setCInactive("#544D46");
  //     setCActive("#7F2627");
  //     setCClosed("#27650A");
  //     setCSuspended("#235F73");
  //     setCSuspendedLock("#235F73");
  //   } else {
  //     setCInactive("#f2f2f2"); //grigio
  //     setCActive("#ffd6d6"); //rosso
  //     setCClosed("#d6f5d6"); //verde
  //     setCSuspended("#d6eaff"); //blu
  //     setCSuspendedLock("#d6eaff");
  //   }
  //   handleRefresh();
  //   console.log(darkMode);
  // }, [darkMode]);
  const isDarkMode = localStorage.getItem("darkMode") === "true";
  const rowStateColorMap = {
    0: isDarkMode ? "#544D46" : "#f2f2f2", // Grigio
    1: isDarkMode ? "#7F2627" : "#ffd6d6", // Rosso
    2: isDarkMode ? "#27650A" : "#d6f5d6", // Verde
    3: isDarkMode ? "#235F73" : "#d6eaff",
    4: isDarkMode ? "#235F73" : "#d6eaff", // Blu
  };

  // console.log(localStorage);
  const initialLanguageSwitch = (() => {
    const savedLanguageSwitch = localStorage.getItem(LANGUAGE_SWITCH_KEY);
    return savedLanguageSwitch !== null
      ? JSON.parse(savedLanguageSwitch)
      : false;
  })();

  const initialSeveritySwitch = (() => {
    const savedSeverity = localStorage.getItem(SEVERITY_SWITCH_KEY);
    return savedSeverity !== null ? JSON.parse(savedSeverity) : true;
  })();

  const [languageSwitch, setLanguageSwitch] = useState(initialLanguageSwitch);
  const [severitySwitch, setSeveritySwitch] = useState(initialSeveritySwitch);
  const [grouping, setGrouping] = useState(initialGrouping);
  const [pinnedColumns, setPinnedColumns] = useState(() =>
    loadFromLocalStorage("pinnedColumns", {
      left: ["original_store", "action"],
      right: [],
    }),
  );
  const [contextMenu, setContextMenu] = useState(null);
  const isGuest = profile.includes("Guest");
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const handleFilterChange = (newFilters) => {
    // Calcola il numero di filtri attivi
    const count = newFilters.length; // Supponendo che `newFilters` sia un array
    setActiveFiltersCount(count);
  };

  useEffect(() => {
    const filteredPinnedColumns = {
      left: pinnedColumns.left.filter(
        (field) =>
          field !== GRID_CHECKBOX_SELECTION_COL_DEF.field &&
          field !== GRID_TREE_DATA_GROUPING_FIELD,
      ),
      right: pinnedColumns.right.filter(
        (field) =>
          field !== GRID_CHECKBOX_SELECTION_COL_DEF.field &&
          field !== GRID_TREE_DATA_GROUPING_FIELD,
      ),
    };
    localStorage.setItem(
      "pinnedColumns",
      JSON.stringify(filteredPinnedColumns),
    );
  }, [pinnedColumns]);

  useEffect(() => {
    localStorage.setItem(LANGUAGE_SWITCH_KEY, JSON.stringify(languageSwitch));
  }, [languageSwitch]);

  useEffect(() => {
    localStorage.setItem(SEVERITY_SWITCH_KEY, JSON.stringify(severitySwitch));
  }, [severitySwitch]);

  useEffect(() => {
    if (deselect) {
      apiRef.current.setRowSelectionModel([]);
      setDeselect(false);
    }
  }, [deselect]);

  const [columnsOrder, setColumnsOrder] = useState(() =>
    loadFromLocalStorage(getColumnsOrderKey(initialGrouping), []),
  );

  useEffect(() => {
    localStorage.setItem(
      getColumnsOrderKey(grouping),
      JSON.stringify(columnsOrder),
    );
  }, [columnsOrder]);

  useEffect(() => {
    const savedColumnsOrder = loadFromLocalStorage(
      getColumnsOrderKey(grouping),
      [],
    );
    setColumnsOrder(savedColumnsOrder);
  }, [grouping]);

  const loadColumnWidths = () => {
    const savedWidths =
      JSON.parse(localStorage.getItem(COLUMN_WIDTHS_KEY)) || {};
    return savedWidths;
  };

  const columns = useMemo(() => {
    const savedWidths = loadColumnWidths();
    const baseColumns = [
      ...(grouping
        ? datagridColumnsWithGrouping(handleOpenMailPopup)
        : datagridColumnsWithNoGrouping()),
      {
        field: "action",
        headerName: I18n.get("Action"),
        resizable: false,
        width: 140,
        renderCell: (params) => {
          if (params.rowNode?.type === "group") {
            return undefined;
          }
          const isInfoSelected =
            selectedIcon?.rowId === params.row.id &&
            selectedIcon.icon === "info" &&
            showPopup;
          const isLaunchSelected =
            selectedIcon?.rowId === params.row.id &&
            selectedIcon.icon === "launch" &&
            showPopup2;
          return (
            <div>
              {/* <Tooltip title={I18n.get("Technical Section")}> */}
              <IconButton
                sx={{
                  width: 30,
                  height: 30,
                  color: isLaunchSelected ? "primary.main" : "inherit", // Change color if selected
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  handlePopUpButtonClick2(params.row, false);
                }}
              >
                <LaunchIcon />
              </IconButton>
              {/*<Tooltip title={I18n.get("Help Center")}> */}
              <IconButton
                sx={{
                  width: 30,
                  height: 30,
                  color: isInfoSelected ? "primary.main" : "inherit", // Change color if selected
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  handlePopUpButtonClick(params.row);
                }}
              >
                {isInfoSelected ? <InfoIcon /> : <InfoOutlinedIcon />}
              </IconButton>
              {/* <Tooltip title={I18n.get("Open in Salesforce")}> */}
              <IconButton
                component="a"
                href={`https://eptarefrigeration.lightning.force.com/${params.row.account_id}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  width: 30,
                  height: 30,
                  color: "inherit", // Change icon color
                }}
                onClick={(event) => event.stopPropagation()} // Prevent row selection on click
              >
                <InsertLinkIcon />
              </IconButton>
              <IconButton
                sx={{
                  width: 30,
                  height: 30,
                  color: "inherit",
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  handleRemoteConnection(params.row, false);
                }}
              >
                <LeakAdd />
              </IconButton>
            </div>
          );
        },
      },
    ];

    // Apply saved widths to all columns
    baseColumns.forEach((col) => {
      if (savedWidths[col.field]) {
        col.width = savedWidths[col.field];
      }
    });

    if (columnsOrder.length > 0) {
      const columnsMap = {};
      baseColumns.forEach((col) => {
        columnsMap[col.field] = col;
      });

      const orderedColumns = columnsOrder
        .map((field) => columnsMap[field])
        .filter((col) => col !== undefined);

      // Add any columns that are not in columnsOrder
      const remainingColumns = baseColumns.filter(
        (col) => !columnsOrder.includes(col.field),
      );

      return [...orderedColumns, ...remainingColumns];
    } else {
      return baseColumns;
    }
  }, [
    grouping,
    handlePopUpButtonClick,
    handlePopUpButtonClick2,
    selectedIcon,
    showPopup,
    showPopup2,
    columnsOrder, // Added columnsOrder to dependencies
  ]);

  const applyStoreFiltering = (value) => {
    let matchesAllFilters = true;

    if (searchText) {
      const searchTextLower = searchText.toLowerCase();
      matchesAllFilters =
        matchesAllFilters &&
        Object.values(value).some((val) =>
          String(val).toLowerCase().includes(searchTextLower),
        );
    }

    if (!severitySwitch) {
      matchesAllFilters = matchesAllFilters && value.severity !== 0;
    }

    const shiftKeys = selectedShifts.map((s) => s.key);
    if (shiftKeys.length > 0 && !shiftKeys.includes("")) {
      matchesAllFilters = matchesAllFilters && shiftKeys.includes(value.shift);
    }

    const controlerKeys = selectedControler.map((c) => c.key).filter(Boolean);

    // "all" must remain exclusive: if present, do not apply controller filtering
    if (controlerKeys.length > 0 && !controlerKeys.includes("")) {
      const hasOthers = controlerKeys.includes("222");
      const specificKeys = controlerKeys.filter((key) => key !== "222");

      const matchesOthers =
        hasOthers &&
        (value.source_flow !== "SQL" || value.alarm_group === "000");

      const matchesSpecific =
        specificKeys.length > 0 &&
        value.source_flow === "SQL" &&
        value.alarm_group != null &&
        specificKeys.some((key) => {
          for (let i = 0; i < key.length; i++) {
            if (key[i] === "1" && value.alarm_group[i] === "1") {
              return true;
            }
          }
          return false;
        });

      matchesAllFilters =
        matchesAllFilters && (matchesOthers || matchesSpecific);
    }

    return matchesAllFilters;
  };

  const handleColumnVisibilityChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(newModel));
  };
  const [isSearchActive, setIsSearchActive] = useState(false);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchText(value);
    setIsSearchActive(value.trim() !== ""); // Mostra l'icona solo se c'è testo
  };

  const getTreeDataPath = (row) => [row.original_store, row.id];
  const filteredRows = state.audit.filter(applyStoreFiltering);

  // const [filteredRows, setFilteredRows] = useState([]);

  // useEffect(() => {
  //   setFilteredRows(state.audit.filter(applyStoreFiltering));
  // }, [searchText]);

  const displayActiveFilters = (filters) => {
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {filters.map((filterName) => {
          const stateOption = stateOptions.find(
            (option) => option.id === filterName,
          );
          const colorMap = isDarkMode
            ? {
                0: "#c0c0c0",
                1: "#cd5c5c",
                2: "#3cb371",
                3: "#1e90ff",
                4: "#4682b4",
                default: "#666666",
              }
            : {
                0: "#888888",
                1: "#b71c1c",
                2: "#388e3c",
                3: "#1976d2",
                4: "#0d47a1",
                default: "#333333",
              };
          const color = stateOption
            ? colorMap[stateOption.id] || colorMap.default
            : colorMap.default;
          return (
            <Chip
              key={filterName}
              label={stateOption ? stateOption.label : filterName}
              sx={{ color: color, borderColor: color }}
              variant="outlined"
              size="small"
            />
          );
        })}
      </Box>
    );
  };
  const formatDateTime = (datetime) => {
    try {
      return format(new Date(datetime), "yyyy-MM-dd HH:mm:ss");
    } catch (error) {
      console.error("Invalid date format:", datetime);
      return datetime; // Ritorna il valore originale se non è una data valida
    }
  };
  const formatDate = (date) => {
    if (filterDuration === "" || filterDuration === null) {
      return dayjs(date).tz().format("YYYY/MM/DD, HH:mm:ss");
    }
    return dayjs.utc(date).tz().format("YYYY/MM/DD, HH:mm:ss");
  };

  // useEffect(() => {
  // if (styleAppliedRef.current) {
  //   document.head.removeChild(styleAppliedRef.current);
  //   styleAppliedRef.current = null;
  // }

  // if (state.audit.length === 0) return;

  // const styleSheet = document.createElement("style");

  // state.audit.forEach((row) => {
  //   const hoverColor = darkenColor(row.color, -10);
  //   const selectedColor = darkenColor(row.color, -20);
  //   const styleRule = `
  //         .row-id-${row.id} {
  //           background-color: ${row.color} !important;
  //         }
  //         .row-id-${row.id}:hover {
  //           background-color: ${hoverColor} !important;
  //         }
  //         .row-id-${row.id}.Mui-selected {
  //           background-color: ${selectedColor} !important;
  //         }
  //       `;
  //   styleSheet.innerText += styleRule;
  // });

  // document.head.appendChild(styleSheet);

  // styleAppliedRef.current = styleSheet;
  //}, [state.audit]);    color in base al campo color
  // Effect for **Italian logic** (when languageSwitch is false)

  const darkModeColors = {
    0: "#343434",
    1: "#585858",
    2: "#6C4C19",
    3: "#544F4F",
    4: "#003669",
    5: "#71671E",
    6: "#824710",
    7: "#733710",
    8: "#874343",
    9: "#6E312E",
    10: "#521F65",
  };

  const durationOptions = [
    { value: 1, label: I18n.get("1 hour") },
    { value: 2, label: I18n.get("2 hours") },
    { value: 3, label: I18n.get("3 hours") },
  ];

  const handleSubmenuOpen = (event) => {
    setSubmenuAnchorEl(event.currentTarget);
    setShowSubmenu(!showSubmenu);
  };

  const handleSubmenuClose = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setShowSubmenu(false);
      setSubmenuAnchorEl(null);
    }, 1); // Delay to allow moving between menu and submenu
  };

  useEffect(() => {
    if (languageSwitch) return;

    if (styleAppliedRef.current) {
      document.head.removeChild(styleAppliedRef.current);
      styleAppliedRef.current = null;
    }

    if (state.audit.length === 0) return;

    const styleSheet = document.createElement("style");

    state.audit.forEach((row) => {
      const rowColor = isDarkMode ? darkModeColors[row.severity] : row.color;

      const hoverColor = isDarkMode ? "" : darkenColor(row.color, -10);
      const selectedColor = isDarkMode ? "" : darkenColor(row.color, -20);
      const styleRule = `
        .row-id-${row.id} {
          background-color:  ${rowColor} !important; 
        }
        .row-id-${row.id}:hover {
          background-color: ${hoverColor} !important;
        }
        .row-id-${row.id}.Mui-selected {
          background-color: ${selectedColor} !important;
        }
      `;
      styleSheet.appendChild(document.createTextNode(styleRule));
    });

    document.head.appendChild(styleSheet);
    styleAppliedRef.current = styleSheet;

    // Cleanup logic
    return () => {
      if (styleAppliedRef.current) {
        document.head.removeChild(styleAppliedRef.current);
        styleAppliedRef.current = null;
      }
    };
  }, [languageSwitch, state.audit, isDarkMode]);

  // Effect for **Deutschland logic** (when languageSwitch is true)
  useEffect(() => {
    if (!languageSwitch) return;

    if (styleAppliedRef.current) {
      document.head.removeChild(styleAppliedRef.current);
      styleAppliedRef.current = null;
    }

    if (state.audit.length === 0) return;

    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";

    state.audit.forEach((row) => {
      const rowColor = rowStateColorMap[row.state] || "#FFFFFF"; // Default to white if state is missing
      const hoverColor = darkenColor(rowColor, -0.1); // Darken by 10%
      const selectedColor = darkenColor(rowColor, -0.2); // Darken by 20%

      const styleRule = `
        .row-id-${row.id} {
          background-color: ${rowColor} !important;
        }
        .row-id-${row.id}:hover {
          background-color: ${hoverColor} !important;
        }
        .row-id-${row.id}.Mui-selected {
          background-color: ${selectedColor} !important;
        }
      `;

      styleSheet.appendChild(document.createTextNode(styleRule));
    });

    document.head.appendChild(styleSheet);
    styleAppliedRef.current = styleSheet;

    // Cleanup logic
    return () => {
      if (styleAppliedRef.current) {
        document.head.removeChild(styleAppliedRef.current);
        styleAppliedRef.current = null;
      }
    };
  }, [languageSwitch, state.audit, isDarkMode]);

  useEffect(() => {
    const savedColumnVisibility = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (savedColumnVisibility) {
      setColumnVisibilityModel(JSON.parse(savedColumnVisibility));
    } else {
      setColumnVisibilityModel({
        [GRID_TREE_DATA_GROUPING_FIELD]: false,
        id: false,
        source_technology: false,
        datetime_close: false,
        // alarm_text: false,
        asset: false,
        serial_number: false,
        billing_city: false,
        billing_street: false,
        retailer: false,
        //phone: false,
        email: false,
        billing_country: false,
        billing_postal_code: false,
      });
    }
  }, []);

  useEffect(() => {
    const parentElement = toolbarRef.current;
    if (!parentElement) {
      console.error("toolbarRef is not correctly attached to any DOM element.");
      return;
    }
    const checkSize = (width) => {
      const breakpoint = 1680;
      setTableParentIsMD(width < breakpoint);
    };
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        checkSize(entry.contentRect.width);
      }
    });
    resizeObserver.observe(parentElement);
    checkSize(parentElement.offsetWidth);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(GROUPING_STATE_KEY, JSON.stringify(grouping));
  }, [grouping]);

  const CustomDatagridToolbar = ({
    grouping,
    setGrouping,
    languageSwitch,
    setLanguageSwitch,
    severitySwitch,
    setSeveritySwitch,
  }) => {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton title={I18n.get("Columns")} />
        <GridToolbarExport
          printOptions={{ disableToolbarButton: true }}
          csvOptions={{
            fileName: I18n.get("Store List"),
            utf8WithBom: true,
          }}
          title={I18n.get("Export")}
        />
        <GridToolbarFilterButton title={I18n.get("Filters")} />
        <Divider
          orientation="vertical"
          flexItem
          sx={{ height: tableParentIsMD ? "50px" : "30px", margin: "0" }}
        />
        <Switch
          checked={grouping}
          onChange={() => setGrouping((prev) => !prev)}
          color="primary"
          inputProps={{ "aria-label": "Grouping Toggle" }}
        />
        <Typography variant="caption">
          {grouping ? I18n.get("Grouping On") : I18n.get("Grouping Off")}
        </Typography>
        {/* Severity toggle button */}
        <Divider
          orientation="vertical"
          flexItem
          sx={{ height: tableParentIsMD ? "50px" : "30px", margin: "0" }}
        />
        <Switch
          checked={languageSwitch}
          onChange={() => setLanguageSwitch((prev) => !prev)} // Assicurati che la funzione venga passatas
          color="primary"
          inputProps={{ "aria-label": "Language Toggle" }}
        />
        <Typography variant="caption">
          {languageSwitch ? I18n.get("State") : I18n.get("Severity")}
        </Typography>
        <Divider
          orientation="vertical"
          flexItem
          sx={{ height: tableParentIsMD ? "50px" : "30px", margin: "0" }}
        />
        <Switch
          checked={severitySwitch}
          onChange={() => setSeveritySwitch((prev) => !prev)} // Assicurati che la funzione venga passatas
          color="primary"
          inputProps={{ "aria-label": "Language Toggle" }}
        />
        <Typography variant="caption">
          {severitySwitch ? I18n.get("Zero on") : I18n.get("Zero off")}
        </Typography>
      </GridToolbarContainer>
    );
  };

  // console.log({ alarmFilterDateRange });

  const [apiStatus, setApiStatus] = useState({
    open: false,
    status: "",
    message: "",
  });
  const [open, setOpen] = useState(false);
  const [dateRange, setDateRange] = useState([
    dayjs(alarmFilterDateRange[0]), // Ensure valid dayjs object
    dayjs(alarmFilterDateRange[1]), // Ensure valid dayjs object
  ]);
  const [selectedCodes, setSelectedCodes] = useState(alarmFilterSwitches);
  const handleOpen = () => {
    setDateRange([
      dayjs(alarmFilterDateRange[0]),
      dayjs(alarmFilterDateRange[1]),
    ]); // Ensure valid dayjs objects
    setSelectedCodes(alarmFilterSwitches);
    setOpen(true);
  };

  const renderToolbar = () => {
    const limitTags = (value, getTagProps) => {
      const limit = 2;
      const visible = value
        .slice(0, limit)
        .map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.key ?? option.label ?? index}
            size="small"
            label={option.label ?? option}
          />
        ));
      if (value.length <= limit) return visible;
      return [
        ...visible,
        <Chip key="more" size="small" label={`+${value.length - limit}`} />,
      ];
    };

    const titleLabel = (() => {
      const base = `${I18n.get("Alarms")} (${filteredRows?.length ?? 0})`;
      if (!accountSelected) return base;
      if (filteredRows?.length > 0)
        return `${base} - ${filteredRows[0].original_store}`;
      return `${base} - ${I18n.get("Account")} ${accountSelected}`;
    })();

    const stableShiftAutocompleteSx = {
      width: 220,
      "& .MuiOutlinedInput-root": {
        height: 46,
        minHeight: 46,
        maxHeight: 46,
        padding: "0 6px",
        alignItems: "center",
      },
      "& .MuiAutocomplete-inputRoot": {
        padding: "0 6px !important",
      },
      "& .MuiAutocomplete-tag": {
        margin: "2px",
        height: 22,
      },
      "& .MuiChip-root": {
        height: 22,
      },
    };

    return (
      <div ref={toolbarRef}>
        <Box sx={{ width: "100%" }}>
          {/* FIRST ROW */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              py: 0.5,
            }}
          >
            <Typography variant="h6" noWrap sx={{ mr: 1 }}>
              {titleLabel}
            </Typography>

            <Tooltip title={I18n.get("Open Alarms Filter")}>
              <IconButton size="small" onClick={handleOpen}>
                <FilterListIcon color="primary" />
              </IconButton>
            </Tooltip>

            {isSearchActive && (
              <Tooltip title={I18n.get("Search Active")} arrow>
                <Box
                  sx={{
                    bgcolor: "secondary.main",
                    color: "white !important",
                    borderRadius: "50%",
                    width: 22,
                    height: 22,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <SearchIcon sx={{ fontSize: 16 }} />
                </Box>
              </Tooltip>
            )}

            {activeFiltersCount > 0 && (
              <Tooltip title={I18n.get("Active Filters")}>
                <Box
                  sx={{
                    bgcolor: "primary.main",
                    color: "white !important",
                    borderRadius: "50%",
                    width: 22,
                    height: 22,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                  }}
                >
                  {activeFiltersCount}
                </Box>
              </Tooltip>
            )}

            <Box sx={{ display: "flex", alignItems: "center" }}>
              {displayActiveFilters(alarmFilterSwitches)}
            </Box>

            <Divider orientation="vertical" flexItem sx={{ height: 28 }} />

            <Typography variant="body2" noWrap>
              {I18n.get("Time Range")} :{" "}
              {isEffectRunning
                ? alarmFilterSwitches.includes(0) ||
                  alarmFilterSwitches.includes(2)
                  ? `${formatDateTime(
                      alarmFilterDateRange[0],
                    )} - ${formatDateTime(alarmFilterDateRange[1])}`
                  : "All - All"
                : alarmFilterSwitches.includes(0) ||
                  alarmFilterSwitches.includes(2)
                ? `${formatDate(alarmFilterDateRange[0])} - ${formatDate(
                    alarmFilterDateRange[1],
                  )}`
                : "All - All"}
            </Typography>

            <Box sx={{ flexGrow: 1 }} />

            <AlarmsFilter
              alarmFilterSwitches={alarmFilterSwitches}
              setAlarmFilterSwitches={setAlarmFilterSwitches}
              alarmFilterDateRange={alarmFilterDateRange}
              setAlarmFilterDateRange={setAlarmFilterDateRange}
              setSelectedCodes={setSelectedCodes}
              selectedCodes={selectedCodes}
              setOpen={setOpen}
              dateRange={dateRange}
              setDateRange={setDateRange}
              open={open}
              language={language}
              fetchLists={fetchLists}
              setIsEffectRunning={setIsEffectRunning}
              isAutoRefresh={isAutoRefresh}
              filterDuration={filterDuration}
              setFilterDuration={setFilterDuration}
              accountSelected={accountSelected}
              setAccountSelected={setAccountSelected}
            />

            {state.lastUpdate && (
              <Typography variant="body2" noWrap>
                {`${state.lastUpdate}`}
              </Typography>
            )}

            <Tooltip title={I18n.get("Refresh")} arrow>
              <IconButton size="small" onClick={handleRefresh}>
                <RefreshOutlined />
              </IconButton>
            </Tooltip>

            {!state.searchFetched && <CircularProgress size="1.5rem" />}

            <ServerFilesMenu />

            <Tooltip title={I18n.get("Active Lock Rules")} arrow>
              <IconButton
                size="small"
                onClick={() => setIsLockDialogOpen(true)}
                color="primary"
              >
                <LockIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={I18n.get("Closure Rules")} arrow>
              <IconButton
                size="small"
                onClick={() => setIsClosureRulesDialogOpen(true)}
                color="primary"
              >
                <RuleFolderIcon />
              </IconButton>
            </Tooltip>

            <Input
              type="text"
              placeholder={I18n.get("Search")}
              onChange={handleSearchChange}
              sx={{ width: 220 }}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton size="small">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              }
            />
          </Box>

          {/* SECOND ROW */}
          <Box
            sx={{
              pt: 1,
              pb: 1,
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <Typography variant="body2" fontWeight="bold">
              {I18n.get("Shift")}
            </Typography>

            <Autocomplete
              multiple
              disableCloseOnSelect
              value={selectedShifts}
              onChange={(event, newValue) => handleChangeShift(event, newValue)}
              options={shiftItem}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.key === value.key}
              renderInput={(params) => <TextField {...params} size="small" />}
              sx={stableShiftAutocompleteSx}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox checked={selected} disableRipple />
                  {option.label}
                </li>
              )}
              renderTags={limitTags}
            />

            <Typography variant="body2" fontWeight="bold">
              {I18n.get("Subsystem")}
            </Typography>

            <Autocomplete
              multiple
              disableCloseOnSelect
              value={selectedControler}
              onChange={(event, newValue) =>
                handleChangeControler(event, newValue)
              }
              options={controlerItem}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.key === value.key}
              renderInput={(params) => <TextField {...params} size="small" />}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox checked={selected} disableRipple />
                  {option.label}
                </li>
              )}
              sx={{ width: 300 }}
            />
          </Box>
        </Box>
      </div>
    );
  };

  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isFromCheckbox, setIsFromCheckbox] = useState(false);
  const [contextRow, setContextRow] = useState(null);
  const [menuRowState, setMenuRowState] = useState(null);

  const handleRowContextMenu = (event) => {
    event.preventDefault();
    const rowId = event.currentTarget.getAttribute("data-id");
    let rowEndData = null;
    if (rowId) {
      const row = filteredRows.find((r) => String(r.id) === String(rowId));
      if (row) rowEndData = row.datetime_end;
    }
    setMenuRowState(rowEndData);
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4,
          }
        : null,
    );
  };

  const handleClose = () => {
    setContextMenu(null);
  };

  const handleColumnOrderChange = (params) => {
    const { column, targetIndex } = params;
    setColumnsOrder((prevOrder) => {
      const field = column.field;
      let newOrder =
        prevOrder.length > 0
          ? [...prevOrder]
          : apiRef.current
              .getAllColumns()
              .map((col) => col.field)
              .filter((field) => field !== "__check__");

      const oldIndex = newOrder.indexOf(field);

      if (oldIndex !== -1) {
        newOrder.splice(oldIndex, 1);
      }

      newOrder.splice(targetIndex - 1, 0, field);

      return newOrder;
    });
  };
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 500,
  });

  const [selectionModel, setSelectionModel] = useState([]);
  const [disableMultiSelect, setDisableMultiSelect] = useState(true);

  useEffect(() => {
    setDisableMultiSelect(true);
    setTimeout(() => {
      window.focus();
    }, 100);
    const handleKeyDown = (event) => {
      // console.log("keydown detected", event.key, "ctrl: ", event.ctrlKey)
      if (event.ctrlKey || event.metaKey) {
        setDisableMultiSelect(false);
      }
    };
    const handleKeyUp = () => {
      // console.log("keyup detected")
      setDisableMultiSelect(true);
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    // console.log("event listeners registered")
    return () => {
      // console.log("event listeners removed")
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  const [selectedId, setSelectedId] = useState(null);
  const handleRowSelection = async (newSelectionModel) => {
    setSelectionModel(newSelectionModel);

    if (newSelectionModel.length > 0) {
      setIsFromCheckbox(true);
      let lastSelectedRow;

      if (
        newSelectionModel[newSelectionModel.length - 1].includes(
          "auto-generated-row-null/",
        )
      ) {
        lastSelectedRow = newSelectionModel[newSelectionModel.length - 2];
      } else {
        lastSelectedRow = newSelectionModel[newSelectionModel.length - 1];
      }
      setSelectedId(lastSelectedRow); //memorizzo l'id della riga selezionata

      if (onRowSelect) {
        onRowSelect(lastSelectedRow);
      }
      const selectedData = await filteredRows.find(
        (row) => row.id === lastSelectedRow,
      );
      handlePopUpButtonClick2(selectedData, true);
      handlePopUpButtonClick(selectedData, true);
    } else {
      setSelectedId(null);
      if (onRowSelect) {
        onRowSelect(null);
      }
    }
  };
  return (
    <Box>
      <Card
        sx={{
          p: 3,
          maxHeight: `${tableHeight}px`,
          overflow: "hidden",
        }}
      >
        {renderToolbar()}
        {isOpen && (
          <ShowMailPopup
            isOpen={isOpen}
            onClose={handleCloseMailPopup}
            mailContent={mailContent}
          />
        )}
        <DataGridPro
          localeText={
            language === "it"
              ? itIT.components.MuiDataGrid.defaultProps.localeText
              : language === "ge"
              ? deDE.components.MuiDataGrid.defaultProps.localeText
              : ""
          }
          disableAutosize
          treeData={grouping}
          rowSelectionPropagation={{
            parents: true,
            descendants: true,
          }}
          isGroupExpandedByDefault={() => true}
          groupingColDef={{
            headerName: I18n.get("Group"),
            width: 400,
            renderCell: (params) => {
              const isParentRow = params.rowNode?.children?.length > 0;
              const childrenCount = isParentRow
                ? params.rowNode.children.length
                : 0;
              const depth = params.rowNode.depth;
              return (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginLeft: depth * 40,
                  }}
                >
                  {isParentRow && (
                    <div
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        marginRight: 8,
                      }}
                    >
                      {<GridExpandMoreIcon />}
                    </div>
                  )}
                  <span>
                    {params.value}
                    {isParentRow && ` (${childrenCount})`}
                  </span>
                </div>
              );
            },
          }}
          loading={!state.fetched || resizeThrottle > 60}
          initialState={{
            density: "compact",
            pinnedColumns: {
              left: [
                GRID_CHECKBOX_SELECTION_COL_DEF.field,
                ...pinnedColumns.left,
              ],
              right: pinnedColumns.right,
            },
            columns: {
              orderedFields: columnsOrder.length > 0 ? columnsOrder : undefined,
            },
          }}
          onPinnedColumnsChange={(newPinnedColumns) => {
            setPinnedColumns(newPinnedColumns);
          }}
          onFilterModelChange={(filterModel) => {
            const activeFilters = filterModel.items.filter(
              (item) => item.value,
            );
            handleFilterChange(activeFilters);
          }}
          getTreeDataPath={getTreeDataPath}
          resizeThrottleMs={resizeThrottle}
          rows={filteredRows}
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
          hideFooterPagination
          hideFooterRowCount
          sx={{
            "& .MuiDataGrid-root, & .MuiDataGrid-footerContainer": {
              minHeight: "2rem",
            },

            "& .MuiDataGrid-cell ": {
              cursor: "default",
              borderRight: "1px solid #e0e0e0",
            },
            borderColor: isDarkMode ? "#6B635A" : "",
            "& .MuiDataGrid-cell, &.MuiDataGrid-columnHeaders": {
              borderColor: isDarkMode ? "#625a52" : "",
            },
            "& .MuiDataGrid-columnSeparator": {
              borderColor: isDarkMode ? "#625a52" : "",
            },
            "--DataGrid-rowBorderColor": isDarkMode ? "#625a52" : "",
            minHeight: "20vh",
            flexFlow: 1,
            height: "100%",
          }}
          //pagination
          //paginationModel={paginationModel}
          //onPaginationModelChange={(model) => setPaginationModel(model)}
          //pageSizeOptions={[10, 25, 50, 100, 300, 500]}
          apiRef={apiRef}
          checkboxSelection
          disableMultipleRowSelection={disableMultiSelect}
          getRowId={(row) => row.id}
          getRowClassName={(params) => `row-with-color row-id-${params.row.id}`}
          components={{
            NoRowsOverlay: () => (
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Typography variant="body2">No Data</Typography>
              </Box>
            ),
          }}
          onRowSelectionModelChange={handleRowSelection}
          rowSelectionModel={selectionModel}
          slots={{
            toolbar: CustomDatagridToolbar,
          }}
          slotProps={{
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
            toolbar: {
              grouping,
              setGrouping,
              languageSwitch, // ✅ Passa il valore del linguaggio
              setLanguageSwitch, // ✅ Passa la funzione per aggiornare il linguaggio
              severitySwitch,
              setSeveritySwitch,
            },
            row: {
              onContextMenu: (e) => handleRowContextMenu(e),
            },
          }}
          onColumnOrderChange={handleColumnOrderChange}
          onColumnResize={handleColumnResize}
        />
        <Menu
          open={contextMenu !== null}
          onClose={handleClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          <MenuItem
            onClick={() => {
              handleTakeInCharge(apiRef.current.getSelectedRows());
              handleClose();
            }}
            disabled={isGuest}
            style={{ fontSize: "18px" }}
          >
            <AssignmentInd style={{ marginRight: 8 }} />
            {I18n.get("Take in charge")}
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleAddRemark(apiRef.current.getSelectedRows());
              handleClose();
            }}
            disabled={isGuest}
            style={{ fontSize: "18px" }}
          >
            <NoteAddIcon style={{ marginRight: 8 }} />
            {I18n.get("Add Remark")}
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleSuspendAlarm(apiRef.current.getSelectedRows());
              handleClose();
            }}
            disabled={isGuest}
            style={{ fontSize: "18px" }}
          >
            <DoDisturbIcon style={{ marginRight: 8 }} />
            {I18n.get("Suspend Alarm")}
          </MenuItem>
          <MenuItem
            onClick={(e) => {
              handleSubmenuOpen(e);
              if (closeTimeoutRef.current)
                clearTimeout(closeTimeoutRef.current);
            }}
            onClose={handleSubmenuClose}
            disabled={isGuest}
            style={{ fontSize: "18px" }}
          >
            <DoDisturbOnIcon style={{ marginRight: 8 }} />
            {I18n.get("Quick Suspend Alarm")}
            <Menu
              anchorEl={submenuAnchorEl}
              open={showSubmenu}
              onClose={handleSubmenuClose}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              MenuListProps={{
                onMouseEnter: () => {
                  if (closeTimeoutRef.current)
                    clearTimeout(closeTimeoutRef.current);
                },
                onMouseLeave: handleSubmenuClose,
              }}
            >
              {durationOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  style={{ fontSize: "18px" }}
                  onClick={() => {
                    setQuickSuspendDuration(option.value);
                    handleQuickSuspendAlarm(apiRef.current.getSelectedRows());
                    handleClose();
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Menu>
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleCloseAlarm(apiRef.current.getSelectedRows());
              handleClose();
            }}
            disabled={isGuest}
            style={{ fontSize: "18px" }}
          >
            <AlarmOffIcon style={{ marginRight: 8 }} />
            {I18n.get("Close Alarm")}
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleOpenNewSalesforceCase(apiRef.current.getSelectedRows());
              handleClose();
            }}
            disabled={isGuest}
            style={{ fontSize: "18px" }}
          >
            <OpenInNewIcon style={{ marginRight: 8 }} />
            {I18n.get("Open New Salesforce Case")}
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleReactiveAlarmSave(apiRef.current.getSelectedRows());
              handleClose();
            }}
            disabled={isGuest}
            style={{ fontSize: "18px" }}
          >
            <PlayCircleIcon style={{ marginRight: 8 }} />
            {I18n.get("Reactivate Alarm")}
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleReassignAlarm(apiRef.current.getSelectedRows());
              handleClose();
            }}
            disabled={isGuest}
            style={{ fontSize: "18px" }}
          >
            <RefreshIcon style={{ marginRight: 8 }} />
            {I18n.get("Reassign Account")}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setIsStepperAlarmDialogOpen(true);
              handleClose();
            }}
            disabled={isGuest}
            style={{ fontSize: "18px" }}
          >
            <AddAlarmIcon style={{ marginRight: 8 }} />
            {I18n.get("Add New Alarm")}
          </MenuItem>
          {menuRowState === null ||
          menuRowState === undefined ||
          menuRowState === "" ? (
            <MenuItem
              onClick={() => {
                handleOpenMailAlarm(apiRef.current.getSelectedRows(), null);
                handleClose();
              }}
              disabled={isGuest}
              style={{ fontSize: "18px" }}
            >
              <EmailRounded style={{ marginRight: 8 }} />
              {I18n.get("View Email")}
            </MenuItem>
          ) : (
            <>
              <MenuItem
                onClick={() => {
                  handleOpenMailAlarm(apiRef.current.getSelectedRows(), true);
                  handleClose();
                }}
                disabled={isGuest}
                style={{ fontSize: "18px" }}
              >
                <EmailRounded style={{ marginRight: 8 }} />
                {I18n.get("View Start Email")}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleOpenMailAlarm(apiRef.current.getSelectedRows(), false);
                  handleClose();
                }}
                disabled={isGuest}
                style={{ fontSize: "18px" }}
              >
                <MarkEmailRead style={{ marginRight: 8 }} />
                {I18n.get("View End Email")}
              </MenuItem>
            </>
          )}
          <MenuItem
            onClick={() => {
              handleOpenSameStoreAlarm(apiRef.current.getSelectedRows());
              handleClose();
            }}
            disabled={isGuest}
            style={{ fontSize: "18px" }}
          >
            <HistoryIcon style={{ marginRight: 8 }} />
            {I18n.get("View History")}
          </MenuItem>
        </Menu>
      </Card>
      <ErrorDialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        message={errorMessage}
      />
      <APIStatusDialog
        open={apiStatus.open}
        status={apiStatus.status}
        message={apiStatus.message}
      />
    </Box>
  );
};

export default AlarmDataGrid;
